import { useCallback, useEffect, useRef, useState } from 'react';
import type { CalendarEventSummary } from '@todo/shared';
import { calendarApi } from '../api/calendar.api';
import { onCalendarEventsChanged } from '../lib/calendarEvents';
import {
  getReminderDecision,
  rememberDismissed,
  rememberSnoozed,
} from '../lib/reminderSnooze';

/**
 * Meeting reminder pop-ups, bottom-right - the calendar's counterpart to
 * ReminderToasts, same mechanics applied to calendar events instead of
 * tasks, plus two triggers that component does not need:
 *
 *   - `onCalendarEventsChanged` fires the instant THIS tab creates or edits
 *     an event with a near-term reminder, so it does not sit unarmed until
 *     the tab happens to regain OS-level focus (which, in one continuous
 *     SPA session with no tab-switching, might be never).
 *   - a light REFRESH_INTERVAL_MS re-check exists because a task's
 *     reminders are yours alone, but an invited attendee can be added to a
 *     meeting from someone ELSE's browser entirely - there is no local
 *     signal for that, so this is the one place in the toast family that
 *     cannot stay purely event-driven.
 *
 * Otherwise identical: on mount (and whenever the tab regains focus) it
 * fetches events in a window wide enough to cover every reminder that
 * could still be relevant - up to HORIZON_MS out, plus MAX_LEAD_MS, since
 * an event's remind_at can sit up to a day before its own starts_at and
 * the API's from/to filters on starts_at, not remind_at - then sets a
 * plain setTimeout for each one. At the moment the reminder falls due, the
 * toast slides in - exact to the second. "Remind in 5 min" is a local
 * snooze (one more timeout); "Dismiss" lets it go - the bell still holds
 * the notification row the server's poller wrote, so nothing is lost.
 */

const SNOOZE_MS = 5 * 60_000;
const HORIZON_MS = 24 * 3_600_000;
/** The longest lead time CalendarEventForm offers ("1 day before"). */
const MAX_LEAD_MS = 24 * 3_600_000;
/** A reminder that fell due while the app was closed still pops, as long as
 *  it is this fresh - older ones stay in the bell only. */
const CATCH_UP_MS = 30 * 60_000;
/** How often to re-check for a meeting someone else just invited you to. */
const REFRESH_INTERVAL_MS = 5 * 60_000;

interface Toast {
  event: CalendarEventSummary;
  snoozed: boolean;
}

export function CalendarReminderToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  /** Reminders already shown this session - a refetch must not re-arm them. */
  const shown = useRef<Set<string>>(new Set());

  const pop = useCallback((event: CalendarEventSummary, snoozed = false) => {
    timers.current.delete(event.id);
    setToasts((current) =>
      current.some((t) => t.event.id === event.id)
        ? current
        : [...current, { event, snoozed }],
    );
  }, []);

  const arm = useCallback(
    (event: CalendarEventSummary) => {
      if (!event.remindAt || shown.current.has(event.id)) return;
      if (timers.current.has(event.id)) return;

      // A decision from a previous page load (Dismiss / Remind in 5 min)
      // survives reloads - without this, refreshing inside the catch-up
      // window brought the card straight back.
      const decision = getReminderDecision('event', event.id, event.remindAt);
      if (decision === 'dismissed') {
        shown.current.add(event.id);
        return;
      }
      if (typeof decision === 'number') {
        const wait = decision - Date.now();
        if (wait <= 0) {
          if (wait > -CATCH_UP_MS) {
            shown.current.add(event.id);
            pop(event, true);
          }
          return;
        }
        timers.current.set(
          event.id,
          setTimeout(() => {
            shown.current.add(event.id);
            pop(event, true);
          }, wait),
        );
        return;
      }

      const delay = new Date(event.remindAt).getTime() - Date.now();
      if (delay > HORIZON_MS) return;

      // Missed while the tab was closed? Show it NOW rather than never.
      if (delay <= 0) {
        if (delay > -CATCH_UP_MS) {
          shown.current.add(event.id);
          pop(event);
        }
        return;
      }

      timers.current.set(
        event.id,
        setTimeout(() => {
          shown.current.add(event.id);
          pop(event);
        }, delay),
      );
    },
    [pop],
  );

  const load = useCallback(() => {
    // Floored to the minute so back-to-back loads build IDENTICAL URLs -
    // that lets the API client's in-flight de-duplication collapse them
    // (a raw Date.now() differs by milliseconds between the two calls).
    const now = Math.floor(Date.now() / 60_000) * 60_000;
    // starts_at can sit up to MAX_LEAD_MS after the reminder that is due
    // NOW, and up to HORIZON_MS+MAX_LEAD_MS after the reminder that is due
    // at the far edge of the horizon - so the fetch window has to reach
    // that far to be sure of catching every relevant event.
    const from = new Date(now - CATCH_UP_MS).toISOString();
    const to = new Date(now + HORIZON_MS + MAX_LEAD_MS).toISOString();
    calendarApi
      .list(from, to)
      .then((events) => {
        for (const event of events) arm(event);
      })
      .catch(() => undefined);
  }, [arm]);

  useEffect(() => {
    load();
    function onVisible() {
      if (document.visibilityState === 'visible') load();
    }
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', onVisible);
    const unsubscribeChanged = onCalendarEventsChanged(load);
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    const pending = timers.current;
    return () => {
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', onVisible);
      unsubscribeChanged();
      clearInterval(interval);
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, [load]);

  /** Hide the card (does NOT persist - snooze reuses this). */
  function dismiss(eventId: string) {
    setToasts((current) => current.filter((t) => t.event.id !== eventId));
  }

  /** The Dismiss button: hide AND remember, so a reload stays quiet. */
  function dismissForGood(event: CalendarEventSummary) {
    if (event.remindAt) rememberDismissed('event', event.id, event.remindAt);
    dismiss(event.id);
  }

  function snooze(event: CalendarEventSummary) {
    dismiss(event.id);
    if (event.remindAt) rememberSnoozed('event', event.id, event.remindAt, Date.now() + SNOOZE_MS);
    timers.current.set(
      event.id,
      setTimeout(() => pop(event, true), SNOOZE_MS),
    );
  }

  if (toasts.length === 0) return null;

  return (
    // Positioning lives in AppLayout's shared toast stack - this group only
    // stacks its own cards.
    <div className="pointer-events-none flex flex-col gap-2">
      {toasts.map(({ event, snoozed }) => (
        <div
          key={event.id}
          role="alert"
          className="pointer-events-auto rounded-xl border border-slate-200 bg-white p-4 shadow-xl ring-1 ring-black/5"
        >
          <div className="flex items-start gap-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-violet-100 text-violet-700">
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4">
                <path d="M5 3v2m10-2v2M3.5 7.5h13M4 5h12a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zM7 10.5h.01M10 10.5h.01M13 10.5h.01M7 13.5h.01M10 13.5h.01"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">
                {snoozed ? 'Meeting reminder · snoozed' : 'Meeting reminder'}
              </p>
              <p className="mt-0.5 truncate text-sm font-medium text-slate-900">
                {event.title}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {new Date(event.startsAt).toLocaleString(undefined, {
                  day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
                })}
                {event.location && ` · ${event.location}`}
              </p>
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => dismissForGood(event)}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
              Dismiss
            </button>
            <button type="button" onClick={() => snooze(event)}
              className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-violet-700">
              Remind in 5 min
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
